import type { Tool, ToolPostPayload } from "@repo/app-types/entities/tool";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import type { FC } from "react";
import { Dialog } from "react-aria-components";
import {
  useGetToolQuery,
  useUpdateToolMutation,
} from "../../../api/entities/tool.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { checkToolHasForm } from "../../tools/hooks/useToolForm.js";
import { ToolEditorSlideoverDialogContent } from "./toolEditorSlideoverDialogContent.js";

export interface ToolEditorSlideoverDialogProps {
  toolId: string;
  onRemoveFromAgent: (toolId: string) => void;
  onAfterSave?: (updatedTool: Tool) => Promise<void>;
}

export const ToolEditorSlideoverDialog: FC<ToolEditorSlideoverDialogProps> = (
  props,
) => {
  const { data: tool, isLoading, isError } = useGetToolQuery(props.toolId);

  const dispatch = useAppDispatch();

  const [updateTool] = useUpdateToolMutation();

  const handleRemoveFromAgent = (toolId: string, close: () => void) => {
    props.onRemoveFromAgent(toolId);
    close();
  };

  const handleSave = async (payload: ToolPostPayload, close: () => void) => {
    try {
      const updatedTool = await updateTool({
        toolId: props.toolId,
        payload,
      }).unwrap();

      showNotification(dispatch, {
        id: `tool-updated-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Tool updated",
        secondaryMessage:
          "Tool has been updated successfully. Publishing changes...",
      });

      try {
        await props.onAfterSave?.(updatedTool);
      } catch (error) {
        console.error("Failed to publish after saving tool:", error);
      }

      close();
    } catch (error) {
      showNotification(dispatch, {
        id: `tool-update-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to update tool",
        secondaryMessage: getApiErrorMessage(error, "Please try again."),
      });

      // Rethrow so the form keeps the user's changes
      throw error;
    }
  };

  const title = tool ? `Edit ${tool.name}` : "Edit tool";

  return (
    <Dialog className="isolate flex h-full flex-col">
      {({ close }) => (
        <>
          <SlideoverDialogTitleHeader isLoading={isLoading}>
            {title}
          </SlideoverDialogTitleHeader>

          {!tool && isLoading ? (
            <SlideoverDialogContent>
              <div className="p-4">
                <DefaultSkeleton />
              </div>
            </SlideoverDialogContent>
          ) : null}

          {!tool && !isLoading && isError ? (
            <SlideoverDialogContent>
              <div className="p-4">
                <p className="text-sm text-red-500">
                  Failed to load tool. Please try again.
                </p>
              </div>
            </SlideoverDialogContent>
          ) : null}

          {tool && !checkToolHasForm(tool) ? (
            <>
              <SlideoverDialogContent>
                <div className="p-4">
                  <p className="text-sm text-muted-foreground">
                    This tool can&apos;t be edited here yet.
                  </p>
                </div>
              </SlideoverDialogContent>
              <SlideoverDialogButtons>
                <Button
                  variant={ButtonVariant.Warning}
                  onClick={() => handleRemoveFromAgent(tool.id, close)}
                >
                  Remove from agent
                </Button>
                <div className="flex flex-auto"></div>
                <Button variant={ButtonVariant.Secondary} onClick={close}>
                  Cancel
                </Button>
              </SlideoverDialogButtons>
            </>
          ) : null}

          {tool && checkToolHasForm(tool) ? (
            <ToolEditorSlideoverDialogContent
              tool={tool}
              onRemoveFromAgent={(toolId) =>
                handleRemoveFromAgent(toolId, close)
              }
              onCancel={close}
              onSave={(payload) => handleSave(payload, close)}
            />
          ) : null}
        </>
      )}
    </Dialog>
  );
};
